import WS from 'ws';
import { SDK_USER_AGENT } from '../../internal/version.js';
import { normalizePairName } from './pair-names.js';
import type { PairSchedule, PriceData, PriceTick, Candle, CandleResolution } from '../types.js';

// ─────────────────────────────────────────────────────────────────────────────
// REST — all prices
// ─────────────────────────────────────────────────────────────────────────────

interface PricesApiResponse {
  prices: RawPriceTick[];
  stale: boolean;
  generatedAt: number;
}

interface RawPriceTick {
  feed_id: string;
  pair: string;
  from: string;
  to: string;
  bid: number;
  mid: number;
  ask: number;
  isMarketOpen: boolean;
  isDayTradingClosed: boolean;
  secondsToToggleIsDayTradingClosed: number;
  timestampSeconds: number;
  schedule?: PairSchedule;
}

/**
 * Fetch the live price feed from `{builderApiUrl}/v1/prices`.
 * Returns a record keyed by raw `${from}/${to}` (e.g. `"BTC/USD"`, `"CL/USD"`).
 * Returns `{}` on 503 (upstream feed not connected).
 */
export async function fetchLivePrices(builderApiUrl: string): Promise<Record<string, PriceData>> {
  const res = await fetch(`${builderApiUrl}/v1/prices`, {
    headers: { 'User-Agent': SDK_USER_AGENT },
  });

  if (res.status === 503) return {};

  if (!res.ok) {
    throw new Error(`Live prices endpoint returned ${res.status} ${res.statusText}`);
  }

  const json = (await res.json()) as PricesApiResponse;
  if (!Array.isArray(json.prices)) {
    throw new Error('Live prices endpoint returned an unexpected payload');
  }

  // Keyed by raw subgraph name (no normalization) so internal pair lookups
  // using rawPair.from / rawPair.to continue to work correctly.
  const result: Record<string, PriceData> = {};
  for (const item of json.prices) {
    if (!item.from || !item.to) continue;
    result[`${item.from}/${item.to}`] = {
      mid: item.mid,
      bid: item.bid,
      ask: item.ask,
      isMarketOpen:                      item.isMarketOpen                      ?? true,
      isDayTradingClosed:                item.isDayTradingClosed                ?? false,
      secondsToToggleIsDayTradingClosed: item.secondsToToggleIsDayTradingClosed ?? -1,
      schedule:                          item.schedule,
    };
  }
  return result;
}

function rawTickToPublic(item: RawPriceTick, pairId?: string): PriceTick {
  const from = normalizePairName(item.from);
  const to   = normalizePairName(item.to);
  return {
    pairId,
    feedId:                            item.feed_id,
    pair:                              `${from}-${to}`,
    from,
    to,
    bid:                               item.bid,
    mid:                               item.mid,
    ask:                               item.ask,
    isMarketOpen:                      item.isMarketOpen                      ?? true,
    isDayTradingClosed:                item.isDayTradingClosed                ?? false,
    secondsToToggleIsDayTradingClosed: item.secondsToToggleIsDayTradingClosed ?? -1,
    timestampSeconds:                  item.timestampSeconds,
    schedule:                          item.schedule,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// REST — OHLC candles
// ─────────────────────────────────────────────────────────────────────────────

interface RawOhlcCandle {
  from:  string;
  to:    string;
  time:  number;  // Unix milliseconds
  open:  number;
  high:  number;
  low:   number;
  close: number;
}

interface OhlcApiResponse {
  data:                 RawOhlcCandle[];
  lastRequestTimestamp: number | null;
}

function rawCandleToPublic(c: RawOhlcCandle): Candle {
  return {
    pairFrom: normalizePairName(c.from),
    pairTo:   normalizePairName(c.to),
    time:     c.time,
    open:     c.open,
    high:     c.high,
    low:      c.low,
    close:    c.close,
  };
}

/**
 * Fetch OHLC candles from `{builderApiUrl}/v1/ohlc`.
 *
 * @param rawPair  - Pair in raw subgraph `"FROM-TO"` format (e.g. `"BTC-USD"`, `"CL-USD"`).
 * @param fromMs   - Start time in Unix milliseconds.
 * @param toMs     - End time in Unix milliseconds.
 * @param resolution - Candle resolution string.
 * @param sets     - Number of candle pages to fetch. Defaults to one page.
 */
export async function fetchCandles(
  builderApiUrl: string,
  rawPair: string,
  fromMs: number,
  toMs: number,
  resolution: CandleResolution,
  sets = 1,
): Promise<Candle[]> {
  const candles: Candle[] = [];
  const seenTimes = new Set<number>();
  let cursorFromMs = fromMs;

  for (let page = 0; page < sets; page++) {
    const pageCandles = await fetchCandlePage(
      builderApiUrl,
      rawPair,
      cursorFromMs,
      toMs,
      resolution,
    );

    if (pageCandles.length === 0) break;

    for (const candle of pageCandles) {
      if (seenTimes.has(candle.time)) continue;
      seenTimes.add(candle.time);
      candles.push(candle);
    }

    if (page === sets - 1) break;

    const nextCursorFromMs = pageCandles[pageCandles.length - 1]?.time;
    if (
      nextCursorFromMs === undefined ||
      !Number.isFinite(nextCursorFromMs) ||
      nextCursorFromMs <= cursorFromMs ||
      nextCursorFromMs >= toMs
    ) {
      break;
    }
    cursorFromMs = nextCursorFromMs;
  }

  return candles;
}

async function fetchCandlePage(
  builderApiUrl: string,
  rawPair: string,
  fromMs: number,
  toMs: number,
  resolution: CandleResolution,
): Promise<Candle[]> {
  const res = await fetch(`${builderApiUrl}/v1/ohlc`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'User-Agent': SDK_USER_AGENT,
    },
    body: JSON.stringify({
      pair:                 rawPair,
      fromTimestampSeconds: Math.floor(fromMs / 1000),
      toTimestampSeconds:   Math.floor(toMs   / 1000),
      resolution,
    }),
  });

  if (!res.ok) {
    throw new Error(`OHLC endpoint returned ${res.status} ${res.statusText}`);
  }

  const json = (await res.json()) as OhlcApiResponse;
  return json.data.map(rawCandleToPublic);
}

// ─────────────────────────────────────────────────────────────────────────────
// WebSocket — live price stream
// ─────────────────────────────────────────────────────────────────────────────

type TickHandler     = (tick: PriceTick) => void;
type SnapshotHandler = (ticks: PriceTick[]) => void;

/**
 * WebSocket client for the Ostium live price stream.
 *
 * ```ts
 * // Via OstiumClient / OstiumSubgraphClient (recommended — pairIds resolved automatically):
 * const stream = client.streamPrices([0, 1]);  // BTC and ETH by pairId
 *
 * stream.onSnapshot(ticks => console.log('snapshot:', ticks.length));
 * stream.onTick(tick => console.log(tick.pair, tick.mid));
 *
 * stream.subscribe([2]);      // add pair 2
 * stream.unsubscribe([0]);    // remove pair 0
 * stream.close();
 * ```
 *
 * The socket connects to `{builderApiUrl}/v1/prices/stream` — `https://` is
 * automatically rewritten to `wss://` (and `http://` to `ws://`). When
 * `streamPrices(pairIds)` is called with ids, the SDK sends
 * `{ type: "subscribe", pairs: ["FROM-TO", ...] }` on `open` (same wire format
 * as {@link OstiumPriceStream.subscribe}).
 *
 * `subscribe` / `unsubscribe` accept the same `pairId` values used everywhere
 * else in the SDK. `PriceTick.pair` / `.from` / `.to` use normalized display
 * names (e.g. `"UK100"` not `"FTSE"`).
 */
export class OstiumPriceStream {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly ws: any; // ws.WebSocket — typed as any to stay compatible with both Node and Bun
  private readonly tickHandlers:     Set<TickHandler>     = new Set();
  private readonly snapshotHandlers: Set<SnapshotHandler> = new Set();
  /** pairId (string) → raw "FROM-TO" name for the WS API. */
  private readonly pairRawNameCache: Map<string, string>;
  /** raw "FROM-TO" name → pairId (string) for mapping incoming ticks back to SDK ids. */
  private readonly rawNamePairIdCache: Map<string, string>;

  private constructor(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ws: any,
    pairRawNameCache: Map<string, string>,
    rawNamePairIdCache: Map<string, string>,
    initialSubscribeRawPairs?: string[],
  ) {
    this.ws = ws;
    this.pairRawNameCache = pairRawNameCache;
    this.rawNamePairIdCache = rawNamePairIdCache;
    if (initialSubscribeRawPairs?.length) {
      ws.once('open', () => {
        ws.send(JSON.stringify({ type: 'subscribe', pairs: initialSubscribeRawPairs }));
      });
    }
    ws.on('message', (data: Buffer | string) => {
      let msg: { type: string; data: unknown };
      try {
        msg = JSON.parse(typeof data === 'string' ? data : data.toString());
      } catch {
        return;
      }
      if (msg.type === 'snapshot' && Array.isArray(msg.data)) {
        const ticks = (msg.data as RawPriceTick[]).map(item =>
          rawTickToPublic(item, this.getPairIdForRawName(`${item.from}-${item.to}`)),
        );
        for (const h of this.snapshotHandlers) h(ticks);
      } else if (msg.type === 'tick' && msg.data) {
        const item = msg.data as RawPriceTick;
        const tick = rawTickToPublic(item, this.getPairIdForRawName(`${item.from}-${item.to}`));
        for (const h of this.tickHandlers) h(tick);
      }
    });
  }

  /**
   * Open a WebSocket connection to the live price stream.
   *
   * Uses the `ws` package which correctly handles case-insensitive `Connection`
   * header values (required for Cloudflare-proxied endpoints).
   *
   * @param builderApiUrl    - Base URL (e.g. `"https://builder.prod.bedrock.ostium.io"`). The `http`/`https`
   *                           scheme is automatically converted to `ws`/`wss`.
   * @param rawPairs         - Optional raw `"FROM-TO"` names (from
   *                           `OstiumSubgraphClient.streamPrices` pairIds). Sent as
   *                           `{ type: "subscribe", pairs }` after the socket opens.
   * @param pairRawNameCache - pairId → raw name map; passed in by the subgraph client so
   *                           `subscribe` / `unsubscribe` can accept pairIds.
   */
  static connect(
    builderApiUrl: string,
    rawPairs?: string[],
    pairRawNameCache: Map<string, string> = new Map(),
  ): OstiumPriceStream {
    const base = builderApiUrl
      .replace(/\/$/, '')
      .replace(/^https?:\/\//, match => match === 'https://' ? 'wss://' : 'ws://');
    const url = `${base}/v1/prices/stream`;
    const initial = rawPairs && rawPairs.length > 0 ? rawPairs : undefined;
    const ws = new WS(url, {
      headers: { 'User-Agent': SDK_USER_AGENT, 'Accept': '*/*' },
    });
    const rawNamePairIdCache = new Map<string, string>();
    for (const [pairId, rawName] of pairRawNameCache.entries()) {
      rawNamePairIdCache.set(rawName, pairId);
    }
    return new OstiumPriceStream(ws, pairRawNameCache, rawNamePairIdCache, initial);
  }

  /**
   * Register a callback that fires on every incoming price tick.
   * Returns an unsubscribe function.
   */
  onTick(handler: TickHandler): () => void {
    this.tickHandlers.add(handler);
    return () => { this.tickHandlers.delete(handler); };
  }

  /**
   * Register a callback that fires once with the full snapshot sent on connect.
   * Returns an unsubscribe function.
   */
  onSnapshot(handler: SnapshotHandler): () => void {
    this.snapshotHandlers.add(handler);
    return () => { this.snapshotHandlers.delete(handler); };
  }

  /** Register a one-time callback that fires when the connection is established. */
  onOpen(handler: () => void): this {
    this.ws.once('open', handler);
    return this;
  }

  /** Register an error callback. */
  onError(handler: (event: Error) => void): this {
    this.ws.on('error', handler);
    return this;
  }

  /** Register a callback that fires when the connection closes. */
  onClose(handler: (code: number, reason: string) => void): this {
    this.ws.on('close', handler);
    return this;
  }

  /**
   * Add pairs to the active filter. Accepts the same `pairId` values used
   * throughout the SDK (numeric string or number).
   * Sends `{ type: "subscribe", pairs: [...rawNames] }` to the server.
   */
  subscribe(pairIds: Array<string | number>): this {
    this.send({ type: 'subscribe', pairs: this.resolveRawNames(pairIds) });
    return this;
  }

  /**
   * Remove pairs from the active filter. Accepts the same `pairId` values used
   * throughout the SDK.
   * Sends `{ type: "unsubscribe", pairs: [...rawNames] }` to the server.
   */
  unsubscribe(pairIds: Array<string | number>): this {
    this.send({ type: 'unsubscribe', pairs: this.resolveRawNames(pairIds) });
    return this;
  }

  /** Close the WebSocket connection. */
  close(): void {
    this.ws.close();
  }

  /** Underlying WebSocket ready state (`0=CONNECTING`, `1=OPEN`, `2=CLOSING`, `3=CLOSED`). */
  get readyState(): number {
    return this.ws.readyState as number;
  }

  private resolveRawNames(pairIds: Array<string | number>): string[] {
    return pairIds.map(id => {
      const cached = this.pairRawNameCache.get(String(id));
      // Fallback: treat unrecognised strings as raw names (e.g. direct "BTC-USD")
      return cached ?? String(id);
    });
  }

  private getPairIdForRawName(rawName: string): string | undefined {
    const cached = this.rawNamePairIdCache.get(rawName);
    if (cached) return cached;

    for (const [pairId, pairRawName] of this.pairRawNameCache.entries()) {
      if (!this.rawNamePairIdCache.has(pairRawName)) {
        this.rawNamePairIdCache.set(pairRawName, pairId);
      }
    }
    return this.rawNamePairIdCache.get(rawName);
  }

  private send(msg: unknown): void {
    if (this.ws.readyState === WS.OPEN) {
      this.ws.send(JSON.stringify(msg));
      return;
    }
    // A filter change issued before the socket finishes connecting would
    // otherwise be dropped silently — which is exactly what the cold
    // pair-cache path in streamPrices() does (it subscribes as soon as the
    // pair list resolves, which can beat the WS handshake).
    if (this.ws.readyState === WS.CONNECTING) {
      this.ws.once('open', () => { this.ws.send(JSON.stringify(msg)); });
    }
  }
}
